// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getNotifyStyle } from "@ces/App/Components/Notify";
import TitleTypography, { TitleTypographyStyles } from "@ces/App/Components/TitleTypography/TitleTypography";
import { TooltipPresentationProps } from "@ces/MobiusOverrides/Tooltip/Tooltip";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@ces/Overrides/Components/QuoteSummaryCard";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes: add numberOfRecords
const enum fields {
    numberOfRecords = "numberOfRecords",
    showQuoteNumber = "showQuoteNumber",
    showType = "showType",
    showStatus = "showStatus",
    showRequested = "showRequested",
    showShipTo = "showShipTo",
    showExpires = "showExpires",
    showSalesReq = "showSalesReq",
    showUser = "showUser",
    showCustomer = "showCustomer",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.showQuoteNumber]: boolean;
        [fields.showType]: boolean;
        [fields.showStatus]: boolean;
        [fields.showRequested]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showExpires]: boolean;
        [fields.showSalesReq]: boolean;
        [fields.showUser]: boolean;
        [fields.showCustomer]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    titleTypographyStyles?: TitleTypographyStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    shipTodataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    statusWrapper?: InjectableCss;
    statusTooltip?: TooltipPresentationProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    noQuotesText: {
        ...getNotifyStyle("info"),
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    // XNG Change: TODO : making the sorting logic if needed
    handleSorting = () => {};

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // XNG-Changes
        const cmsProperties = {
            showQuoteNumber: this.props.fields.showQuoteNumber,
            showType: this.props.fields.showType,
            showStatus: this.props.fields.showStatus,
            showRequested: this.props.fields.showRequested,
            showExpires: this.props.fields.showExpires,
            showShipTo: this.props.fields.showShipTo,
            showSalesReq: this.props.fields.showSalesReq,
            showUser: this.props.fields.showUser,
            showCustomer: this.props.fields.showCustomer,
        };

        return (
            <>
                <TitleTypography
                    extendedStyles={styles.titleTypographyStyles}
                    linkText={translate("View All Open Quotes")}
                    link={myQuotesPageUrl}
                    showIcon={true}
                >
                    {translate("Open Quotes")}
                </TitleTypography>
                {quotesDataView.value.length === 0 && (
                    <GridItem {...styles.noQuotesGridItem}>
                        <Typography {...styles.noQuotesText}>{translate("No Quotes found")}</Typography>
                    </GridItem>
                )}
                {quotesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {cmsProperties.showQuoteNumber && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Quote #")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showType && settingsCollection.quoteSettings.jobQuoteEnabled && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Type")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showStatus && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Status")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showRequested && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Requested Date")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showExpires && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Expires")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showSalesReq && session.isSalesPerson && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Sales Req")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showUser && session.isSalesPerson && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("User")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showCustomer && (
                                <DataTableHeader {...styles.dataTableHeaders} tight onSortClick={this.handleSorting}>
                                    {translate("Customer")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showShipTo && !session.isSalesPerson && (
                                <DataTableHeader {...styles.shipTodataTableHeaders} onSortClick={this.handleSorting}>
                                    {translate("Ship To/Pick Up")}
                                </DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {quotesDataView.value.map(quote => (
                                <QuoteSummaryCard
                                    key={quote.id}
                                    quote={quote}
                                    session={session}
                                    quoteSettings={settingsCollection.quoteSettings}
                                    extendedStyles={styles.quoteSummaryCard}
                                    cmsProperties={cmsProperties}
                                />
                            ))}
                        </DataTableBody>
                    </DataTable>
                )}
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.showQuoteNumber,
                displayName: "Quote #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showType,
                displayName: "Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showRequested,
                displayName: "Requested",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showExpires,
                displayName: "Expires",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showSalesReq,
                displayName: "Sales Req",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCustomer,
                displayName: "Customer",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showUser,
                displayName: "User",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
